import { useState, useEffect } from 'react';
import { getPlaylistsData, PlaylistData } from '@/api/spotify';

interface PlaylistConfig {
  id: string;
  fallbackGradient: string;
}

const PLAYLIST_CONFIGS: PlaylistConfig[] = [
  { id: '5YO3Clj7ARarnzwvDveVDP', fallbackGradient: 'from-purple-600 to-pink-600' },
  { id: '51yPhPOiRd5XoqFbkVFBDw', fallbackGradient: 'from-amber-500 to-yellow-500' },
  { id: '0DiUicjYoiIRkWBIt3wJgT', fallbackGradient: 'from-red-600 to-orange-600' },
  { id: '2VrPiprPMCgF72XR7WZugl', fallbackGradient: 'from-amber-600 to-yellow-600' },
  { id: '40BZXKD8T2I8IKZR02mr65', fallbackGradient: 'from-slate-600 to-zinc-500' },
  { id: '5OWoMLd2OCqOV4sKgEtUUe', fallbackGradient: 'from-green-600 to-emerald-500' },
  { id: '15BmopJ3SYNOxe7zuhRMBE', fallbackGradient: 'from-blue-700 to-indigo-600' },
  { id: '1XS43Dnst3b88mt5ocSPHu', fallbackGradient: 'from-purple-700 to-violet-600' },
  { id: '3AZWIxvNJcO9J99SypKHnQ', fallbackGradient: 'from-red-700 to-green-700' },
  { id: '3NsrhctlJhDR64zG7MjDZD', fallbackGradient: 'from-indigo-600 to-violet-600' },
  { id: '3v1bibTavdsuesNp7yxCN9', fallbackGradient: 'from-indigo-500 to-blue-600' },
  { id: '5lfnzctTH2NQ6FI5g8GBrl', fallbackGradient: 'from-cyan-500 to-blue-600' },
  { id: '3clufPVbnEyV033YL0kC6I', fallbackGradient: 'from-violet-500 to-purple-600' },
];

const PLAYLIST_IDS = PLAYLIST_CONFIGS.map(p => p.id);

const PlaylistGrid = () => {
  const [playlists, setPlaylists] = useState<(PlaylistData & { fallbackGradient: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    getPlaylistsData(PLAYLIST_IDS).then(data => {
      setPlaylists(
        data.map((p, i) => ({ ...p, fallbackGradient: PLAYLIST_CONFIGS[i].fallbackGradient }))
      );
      setLoading(false);
    });
  }, []);

  return (
    <div className="mb-16">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          Artistspellistor
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280' }}>
          Utforska musik per genre — öppnas i Spotify
        </p>
      </div>

      {/* Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
        }}
        className="playlist-grid"
      >
        {loading
          ? Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="playlist-skeleton"
                style={{
                  borderRadius: 12,
                  aspectRatio: '1',
                  background: 'rgba(0,0,0,0.06)',
                  animation: 'playlist-pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.08}s`,
                }}
              />
            ))
          : playlists.map(playlist => (
              <a
                key={playlist.id}
                href={playlist.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', display: 'block' }}
                onMouseEnter={() => setHoveredId(playlist.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div
                  style={{
                    position: 'relative',
                    borderRadius: 12,
                    overflow: 'hidden',
                    aspectRatio: '1',
                    cursor: 'pointer',
                  }}
                >
                  {/* Cover image or fallback gradient */}
                  {playlist.image ? (
                    <img
                      src={playlist.image}
                      alt={playlist.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transform: hoveredId === playlist.id ? 'scale(1.05)' : 'scale(1)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  ) : (
                    <div
                      className={`bg-gradient-to-br ${playlist.fallbackGradient}`}
                      style={{ width: '100%', height: '100%' }}
                    />
                  )}

                  {/* Gradient overlay */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '60%',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
                    }}
                  />

                  {/* Spotify play badge */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#1DB954',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>

                  {/* Text */}
                  <div style={{ position: 'absolute', bottom: 10, left: 10, right: 10 }}>
                    <p style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'white',
                      margin: 0,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                    }}>
                      {playlist.name || '—'}
                    </p>
                    {playlist.trackCount > 0 && (
                      <p style={{
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.6)',
                        margin: '2px 0 0',
                      }}>
                        {playlist.trackCount} låtar
                      </p>
                    )}
                  </div>

                  {/* Hover overlay */}
                  {hoveredId === playlist.id && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(29,185,84,0.28)',
                        backdropFilter: 'blur(2px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                        Lyssna nu →
                      </span>
                    </div>
                  )}
                </div>
              </a>
            ))}
      </div>

      <style>{`
        @keyframes playlist-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        @media (max-width: 640px) {
          .playlist-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PlaylistGrid;
